'use client';

import React from "react";
import { IPage, IPlugin } from "../../types";
import { StyledDiv } from "./widgetFeatures.styles";
import { Feature } from "../feature/feature.comp";
import Image from "next/image";

// Outline-icon props shared with the rest of the page so the
// "Also included in the editor" pills match the page's icon system
// instead of pulling mismatched .webp assets from the CMS.
// 28x28 matches the WhyUseWidget branded ICON_PROPS used by the
// "What you get with..." section, so icon size + stroke convention is
// identical across every card grid on the enriched hub layout. Per
// [[feedback_grid_symmetry]] — icons follow the same convention too.
const SLIM_BOX_ICON_PROPS = {
  width: 28,
  height: 28,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// Mapping from box-feature title to inline outline SVG. Keys must
// match the title exactly (case-sensitive) as stored in MongoDB.
const SLIM_BOX_ICONS: Record<string, React.ReactNode> = {
  "Date & Time Display Settings": (
    <svg {...SLIM_BOX_ICON_PROPS} aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="7" />
      <line x1="16" y1="3" x2="16" y2="7" />
      <polyline points="11 13 11 16 14 16" />
    </svg>
  ),
  "Customize Character Display Length": (
    <svg {...SLIM_BOX_ICON_PROPS} aria-hidden="true">
      <polyline points="4 7 4 4 20 4 20 7" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  ),
  "Full RTL Support": (
    <svg {...SLIM_BOX_ICON_PROPS} aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="9" y1="12" x2="21" y2="12" />
      <line x1="6" y1="18" x2="21" y2="18" />
    </svg>
  ),
  "Easy To Use widget": (
    <svg {...SLIM_BOX_ICON_PROPS} aria-hidden="true">
      <path d="M12 3l1.8 5.2 5.2 1.8-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
      <path d="M19 16l0.7 1.8 1.8 0.7-1.8 0.7L19 21l-0.7-1.8L16.5 18.5l1.8-0.7z" />
    </svg>
  ),
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stripHtml(html: string): string {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function textToParagraphs(text: string): string {
  if (!text) return "";
  if (/<(p|ul|ol|h[1-6]|br|div|strong|em|a)\b/i.test(text)) return text;
  return text
    .split(/\n\s*\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br />")}</p>`)
    .join("");
}

export interface IExtraBoxFeature {
  title: string;
  text: string;
  imgUrl?: string;
}

// Per-hub icon override map. Keys are box-feature titles (case-sensitive,
// matching the DB title exactly). Values are inline outline SVGs that
// follow the SLIM_BOX_ICON_PROPS convention — same line-art style as the
// "What you get with..." section icons. Per [[feedback_grid_symmetry]],
// the icon convention is unified across every card grid on the enriched
// layout. Use this prop to replace the CMS-stored colorful raster images
// (Image fallback) with outline SVGs for hub-specific box titles.
export type ISlimBoxIcons = Record<string, React.ReactNode>;

export const WidgetFeatures = ({
  widget,
  page,
  parentHeading,
  slim = false,
  extraBoxFeatures,
  slimBoxIcons,
}: {
  widget: IPlugin;
  page?: IPage;
  parentHeading?: string;
  slim?: boolean;
  extraBoxFeatures?: IExtraBoxFeature[];
  slimBoxIcons?: ISlimBoxIcons;
}) => {
  // Section features
  const sectionFeaturesApp = widget.features.filter(
    (feature) => feature.type === "section" || !feature.type
  );
  const sectionFeaturesPage = page?.rewrites.features
    .map((f, idx) => ({
      ...f,
      type: widget.features[idx]?.type,
      imgUrl: widget.features[idx]?.imgUrl || f.imgUrl,
    }))
    .filter((feature) => feature.type === "section" || !feature.type);
  const finalFeaturesSections =
    (!page ? sectionFeaturesApp : sectionFeaturesPage) || [];
  const middle = Math.ceil(finalFeaturesSections.length / 2);
  const first = finalFeaturesSections.slice(0, middle);
  const second = finalFeaturesSections.slice(middle);

  // Box features
  const boxFeaturesApp = widget.features.filter(
    (feature) => feature.type === "box"
  );
  const boxFeaturesPage = page?.rewrites.features
    .map((f, idx) => ({
      ...f,
      type: widget.features[idx]?.type,
      imgUrl: widget.features[idx]?.imgUrl || f.imgUrl,
    }))
    .filter((feature) => feature.type === "box");
  const baseFeaturesBox = (!page ? boxFeaturesApp : boxFeaturesPage) || [];
  // Append any caller-supplied extras (used by SEO hub enrichments to keep
  // the 2x2 grid symmetric when the DB box count is odd).
  const finalFeaturesBox = extraBoxFeatures
    ? [
        ...baseFeaturesBox,
        ...extraBoxFeatures.map((b) => ({
          ...b,
          type: "box" as const,
          imgUrl: b.imgUrl || "",
        })),
      ]
    : baseFeaturesBox;

  const childHeadingLevel = parentHeading ? "h3" : "h2";
  const renderText = (t: string) => textToParagraphs(t);

  // ============================================================
  // SLIM MODE — compact 3-column feature gallery.
  // Replaces the alternating full-width image+text rows with a
  // uniform card grid: every image renders at the same 16:10
  // aspect ratio with object-fit: cover, so source assets that
  // include decorative whitespace, parallax stacks, or floating
  // elements get cropped to a consistent shape. Used on
  // /widgets/instagram-feed only.
  // ============================================================
  if (slim) {
    return (
      <StyledDiv className="slim">
        {parentHeading && <h2 className="features-heading">{parentHeading}</h2>}

        <ul className="feature-grid">
          {finalFeaturesSections.map((feature) => (
            <li key={feature.title} className="feature-card">
              <div className="img-wrap">
                <Image
                  src={feature.imgUrl}
                  alt={`${widget?.name} - ${feature.title}`}
                  width={685}
                  height={428}
                  sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                />
              </div>
              <div className="card-body">
                <h3>{feature.title}</h3>
                <p>{stripHtml(feature.text)}</p>
              </div>
            </li>
          ))}
        </ul>

        {finalFeaturesBox.length > 0 && (
          <>
            <h2 className="extras-heading">Also included in the editor</h2>
            <div className="benefits">
              {finalFeaturesBox.map((feature) => (
                <div key={feature.title} className="card">
                  <span className="icon-wrap" aria-hidden="true">
                    {slimBoxIcons?.[feature.title] ||
                      SLIM_BOX_ICONS[feature.title] ||
                      (feature.imgUrl && (
                        <Image
                          src={feature.imgUrl}
                          alt=""
                          width={30}
                          height={30}
                        />
                      ))}
                  </span>
                  <h3>{feature.title}</h3>
                  <p>{stripHtml(feature.text)}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </StyledDiv>
    );
  }

  // ============================================================
  // DEFAULT MODE — alternating full-width rows (every other widget page)
  // ============================================================
  return (
    <StyledDiv>
      {parentHeading && <h2 className="features-heading">{parentHeading}</h2>}
      {first.map((feature, idx) => (
        <Feature
          key={`${feature.title}-app`}
          title={feature.title}
          text={renderText(feature.text)}
          isHtml
          position={idx % 2 === 0 ? "ltr" : "rtl"}
          alt={`${widget?.name} - ${feature.title}`}
          img={feature.imgUrl}
          headingLevel={childHeadingLevel}
        />
      ))}
      {finalFeaturesBox.length > 0 && (
        <div className="benefits">
          {finalFeaturesBox.map((feature) => (
            <div key={feature.title} className="card">
              <Image
                src={feature.imgUrl}
                alt={`${widget?.name} - ${feature.title}`}
                width={30}
                height={30}
              />
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </div>
          ))}
        </div>
      )}
      {second.map((feature, idx) => (
        <Feature
          key={`${feature.title}-app`}
          title={feature.title}
          text={renderText(feature.text)}
          isHtml
          position={idx % 2 === 0 ? "ltr" : "rtl"}
          alt={`${widget?.name} - ${feature.title}`}
          img={feature.imgUrl}
          headingLevel={childHeadingLevel}
        />
      ))}
    </StyledDiv>
  );
};
